import React, { useState, useEffect, useDeferredValue, useMemo, useCallback, memo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp,
  TrendingDown,
  ArrowDropUp,
  ArrowDropDown,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { budgetService, Budget, BudgetRemainder, CreateBudgetRequest, UpdateBudgetRequest } from '../services/budgetService';
import { MonthlySpendCategory } from '../services/plaidService';
import { cachedMonthlySpend, cachedYearlySalary } from '../services/prefetch';
import SpendingByCategoryChart from './SpendingByCategoryChart';

interface MonthlyIncomePoint {
  month: string;
  income: number;
}

// ----- Pure helpers hoisted to module scope so memoized children keep
// stable references and don't reconcile on unrelated parent re-renders. -----

const calculatePercentage = (current: string | number, total: string | number): number => {
  const currentNum = parseFloat(String(current));
  const totalNum = parseFloat(String(total));
  return totalNum > 0 ? (currentNum / totalNum) * 100 : 0;
};

const getStatusColor = (percentage: number): string => {
  if (percentage >= 100) return '#c43e3e';
  if (percentage >= 80) return '#b8842a';
  return '#0f7a5a';
};

const getStatusBg = (percentage: number): string => {
  if (percentage >= 100) return '#fbeae9';
  if (percentage >= 80) return '#fbf2e3';
  return '#e8f3ee';
};

const getPacing = (percentage: number, period: string) => {
  const now = new Date();
  let daysElapsed: number;
  let daysInPeriod: number;
  if (period === 'yearly') {
    const start = new Date(now.getFullYear(), 0, 0);
    daysElapsed = Math.ceil((now.getTime() - start.getTime()) / 86400000);
    const yr = now.getFullYear();
    daysInPeriod = ((yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0) ? 366 : 365;
  } else {
    daysElapsed = now.getDate();
    daysInPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }
  const expectedPct = (daysElapsed / daysInPeriod) * 100;
  const diff = percentage - expectedPct;
  return { daysElapsed, daysInPeriod, expectedPct, diff };
};

const formatCurrency = (amount: string | number): string => {
  return parseFloat(String(amount)).toFixed(2);
};

const CATEGORY_EMOJI: { [key: string]: string } = {
  groceries: '🛒',
  dining: '🍽️',
  entertainment: '🎬',
  transportation: '🚗',
  shopping: '🛍️',
  utilities: '💡',
  healthcare: '🏥',
  travel: '✈️',
  other: '📦',
};

const getCategoryEmoji = (category: string): string =>
  CATEGORY_EMOJI[category.toLowerCase()] || '📦';

// ----- Memoized per-budget card. Only re-renders when its own props change,
// so editing one budget no longer reconciles every other card. -----

interface BudgetCardProps {
  budget: Budget;
  idx: number;
  draftValue: number | undefined;
  isSaving: boolean;
  onAdjust: (budget: Budget, delta: number) => void;
  onSetDraft: (id: number, value: number) => void;
  onReset: (id: number) => void;
  onSave: (budget: Budget) => void;
}

const BudgetCard = memo<BudgetCardProps>(function BudgetCard({
  budget,
  idx,
  draftValue,
  isSaving,
  onAdjust,
  onSetDraft,
  onReset,
  onSave,
}) {
  const percentage = calculatePercentage(budget.current_spending, budget.budget_amount);
  const remaining = parseFloat(String(budget.budget_amount)) - parseFloat(String(budget.current_spending));
  const statusColor = getStatusColor(percentage);
  const statusBg = getStatusBg(percentage);
  const pacing = getPacing(percentage, budget.time_period);
  const onPace = Math.abs(pacing.diff) < 3;
  const aheadOfPace = pacing.diff > 0;
  const originalAmount = parseFloat(String(budget.budget_amount));
  const editingValue = draftValue ?? originalAmount;
  const isDirty = draftValue != null && Math.abs(draftValue - originalAmount) > 0.005;

  return (
    <Box
      sx={{
        borderBottom: '1px solid #f0f0f0',
        borderRight: {
          xs: 'none',
          sm: idx % 2 === 0 ? '1px solid #f0f0f0' : 'none',
          md: (idx % 3 !== 2) ? '1px solid #f0f0f0' : 'none',
        },
        px: { xs: 2, sm: 2.5 },
        py: { xs: 2, sm: 2.25 },
        transition: 'background-color 0.15s ease',
        bgcolor: isDirty ? '#fbf7e8' : 'transparent',
        '&:hover': { bgcolor: isDirty ? '#fbf2d4' : '#fafaf6' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.25,
            bgcolor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '15px',
            flexShrink: 0,
          }}
        >
          {getCategoryEmoji(budget.category)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#0a0a0a', textTransform: 'capitalize', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {budget.category}
          </Typography>
          <Typography variant="caption" sx={{ color: '#8a8a8a', textTransform: 'capitalize', fontSize: '0.6875rem' }}>
            {budget.time_period} · ${formatCurrency(budget.current_spending)} spent
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', height: 5, bgcolor: '#f0f0f0', borderRadius: 3, overflow: 'visible', mb: 0.75 }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            bgcolor: statusColor,
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
        {pacing.expectedPct < 100 && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              left: `${pacing.expectedPct}%`,
              width: 2,
              height: 9,
              bgcolor: '#5c5c5c',
              borderRadius: 1,
              transform: 'translateX(-50%)',
            }}
            title={`Expected pace: ${pacing.expectedPct.toFixed(0)}%`}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: statusColor, fontVariantNumeric: 'tabular-nums', fontSize: '0.7rem' }}>
          {percentage.toFixed(0)}% used
        </Typography>
        <Box component="span" sx={{ color: '#c4c4c4', fontSize: '0.7rem' }}>·</Box>
        {onPace ? (
          <Typography variant="caption" sx={{ color: '#5c5c5c', fontSize: '0.7rem' }}>on pace</Typography>
        ) : aheadOfPace ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: '#c43e3e' }}>
            <TrendingUp sx={{ fontSize: 10 }} />
            <Typography variant="caption" sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums', fontSize: '0.7rem' }}>
              {Math.abs(pacing.diff).toFixed(0)}% over pace
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: '#0f7a5a' }}>
            <TrendingDown sx={{ fontSize: 10 }} />
            <Typography variant="caption" sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums', fontSize: '0.7rem' }}>
              {Math.abs(pacing.diff).toFixed(0)}% under pace
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            ml: 'auto',
            color: statusColor,
            bgcolor: statusBg,
            fontWeight: 600,
            fontSize: '0.6875rem',
            px: 0.75,
            py: 0.125,
            borderRadius: 0.75,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {remaining >= 0
            ? `+$${formatCurrency(remaining)} left`
            : `−$${formatCurrency(Math.abs(remaining))} over`}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            border: '1px solid',
            borderColor: isDirty ? '#b8842a' : '#eaeaea',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#ffffff',
          }}
        >
          <IconButton
            size="small"
            onClick={() => onAdjust(budget, -10)}
            disabled={isSaving}
            sx={{ borderRadius: 0, px: 0.5, color: '#5c5c5c', '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            <ArrowDropDown sx={{ fontSize: 20 }} />
          </IconButton>
          <TextField
            type="number"
            size="small"
            value={editingValue}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onSetDraft(budget.id, Math.max(0, v));
              else onSetDraft(budget.id, 0);
            }}
            disabled={isSaving}
            InputProps={{
              startAdornment: <InputAdornment position="start" sx={{ mr: 0.25 }}>$</InputAdornment>,
            }}
            sx={{
              width: 90,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                fontSize: '0.75rem',
                fontVariantNumeric: 'tabular-nums',
                '& fieldset': { border: 'none' },
                '& input': { py: 0.5, px: 0.25, textAlign: 'right' },
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => onAdjust(budget, 10)}
            disabled={isSaving}
            sx={{ borderRadius: 0, px: 0.5, color: '#5c5c5c', '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            <ArrowDropUp sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        {isDirty && (
          <Typography
            variant="caption"
            sx={{
              color: editingValue < originalAmount ? '#0f7a5a' : '#c43e3e',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '0.7rem',
            }}
          >
            {editingValue < originalAmount ? '−' : '+'}${Math.abs(editingValue - originalAmount).toFixed(2)}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
          {isDirty && (
            <Button
              size="small"
              variant="text"
              onClick={() => onReset(budget.id)}
              disabled={isSaving}
              startIcon={<UndoIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: '#5c5c5c',
                minWidth: 0,
                px: 1,
                py: 0.25,
              }}
            >
              Reset
            </Button>
          )}
          <Button
            size="small"
            variant={isDirty ? 'contained' : 'outlined'}
            onClick={() => onSave(budget)}
            disabled={!isDirty || isSaving}
            startIcon={isSaving ? <CircularProgress size={11} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              minWidth: 0,
              px: 1.25,
              py: 0.25,
            }}
          >
            Save Budget
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

// ----- Memoized projected-savings chart. Recharts is expensive — only
// reconcile when the deferred preview/saved values actually change. -----

interface ProjectedSavingsChartProps {
  data: { month: string; savings: number; savingsApr: number; preview?: number }[];
  hasDraft: boolean;
  savedColor: string;
  apr: number;
}

const ProjectedSavingsChart = memo<ProjectedSavingsChartProps>(function ProjectedSavingsChart({
  data,
  hasDraft,
  savedColor,
  apr,
}) {
  const aprLabel = `Savings @ ${apr.toFixed(2).replace(/\.?0+$/, '')}% APR`;
  // Thin out X-axis ticks for long horizons so labels don't overlap (~ every 12 months for 5Y+).
  const xInterval = data.length > 60 ? 11 : data.length > 24 ? 5 : 0;
  // Switch dot rendering: too many dots on long horizons looks like spaghetti.
  const showDots = data.length <= 24;
  return (
    <>
      {/* Legend: solid saved line, always-on grey dashed APR line, optional preview */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 18, height: 2, bgcolor: savedColor, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: '#5c5c5c', fontSize: '0.75rem' }}>
            Saved budget
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 18, height: 0, borderTop: '2px dashed #8a8a8a' }} />
          <Typography variant="caption" sx={{ color: '#5c5c5c', fontSize: '0.75rem' }}>
            {aprLabel}
          </Typography>
        </Box>
        {hasDraft && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 18, height: 0, borderTop: `2px dotted ${savedColor}` }} />
            <Typography variant="caption" sx={{ color: '#5c5c5c', fontSize: '0.75rem' }}>
              Preview (unsaved)
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ height: 280, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: '#5c5c5c' }}
              tickLine={false}
              axisLine={{ stroke: '#eaeaea' }}
              interval={xInterval}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#5c5c5c' }}
              tickLine={false}
              axisLine={{ stroke: '#eaeaea' }}
              tickFormatter={(value: number) => {
                const abs = Math.abs(value);
                if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
                if (abs >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                return `$${value}`;
              }}
            />
            <RechartsTooltip
              cursor={{ stroke: '#eaeaea' }}
              formatter={(value: any, name: any) => {
                const label =
                  name === 'preview'
                    ? 'Preview (unsaved)'
                    : name === 'savingsApr'
                    ? aprLabel
                    : 'Saved budget';
                return [
                  `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`,
                  label,
                ];
              }}
            />
            <Line
              type="monotone"
              dataKey="savings"
              name="savings"
              stroke={savedColor}
              strokeWidth={2}
              dot={showDots ? { r: 3, fill: savedColor } : false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="savingsApr"
              name="savingsApr"
              stroke="#8a8a8a"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={showDots ? { r: 3, fill: '#8a8a8a' } : false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {hasDraft && (
              <Line
                type="monotone"
                dataKey="preview"
                name="preview"
                stroke={savedColor}
                strokeWidth={2}
                strokeDasharray="2 4"
                dot={showDots ? { r: 3, fill: savedColor } : false}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
});

// ----- Memoized monthly-income chart. Doesn't depend on drafts at all,
// so it should never reconcile when budget amounts change. -----

const MonthlyIncomeChart = memo<{ data: MonthlyIncomePoint[] }>(function MonthlyIncomeChart({ data }) {
  return (
    <Box sx={{ height: 260, mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: '#5c5c5c' }}
            tickLine={false}
            axisLine={{ stroke: '#eaeaea' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#5c5c5c' }}
            tickLine={false}
            axisLine={{ stroke: '#eaeaea' }}
            tickFormatter={(value: number) =>
              value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`
            }
          />
          <RechartsTooltip
            cursor={{ fill: 'rgba(15,122,90,0.06)' }}
            formatter={(value: any) => [
              `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
              'Income',
            ]}
          />
          <Bar dataKey="income" fill="#0f7a5a" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
});

const Budgets: React.FC = () => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Form state
  const [formData, setFormData] = useState<CreateBudgetRequest>({
    category: '',
    budget_amount: 0,
    time_period: 'monthly',
    alert_thresholds: [80, 100, 120],
  });

  const [yearlyIncome, setYearlyIncome] = useState<MonthlyIncomePoint[]>([]);
  const [yearlyIncomeLoading, setYearlyIncomeLoading] = useState(true);

  const [remainder, setRemainder] = useState<BudgetRemainder | null>(null);
  const [remainderLoading, setRemainderLoading] = useState(true);

  // Local-only edits to budget amounts. Drives the live chart preview before Save.
  const [draftAmounts, setDraftAmounts] = useState<Record<number, number>>({});
  // Mirror in a ref so stable callbacks (saveDraft) can read latest drafts
  // without invalidating their identity and busting card memoization.
  const draftAmountsRef = React.useRef(draftAmounts);
  React.useEffect(() => { draftAmountsRef.current = draftAmounts; }, [draftAmounts]);
  // Deferred copy used by the expensive chart render so keystrokes stay snappy —
  // the TextField/input always reads `draftAmounts`, the chart reads this.
  const deferredDrafts = useDeferredValue(draftAmounts);
  const [savingBudgetId, setSavingBudgetId] = useState<number | null>(null);

  // Annual percentage rate (in %) used to compute the grey-dashed
  // savings-account projection. Defaults to 3.5% (typical HYSA). User-editable.
  const [apr, setApr] = useState<number>(3.5);
  // Defer so the Recharts re-render doesn't fight per-keystroke input updates.
  const deferredApr = useDeferredValue(apr);

  // Projection horizon in months. 12 (1Y), 60 (5Y), or 120 (10Y).
  const [horizonMonths, setHorizonMonths] = useState<number>(12);

  const [neededSpend, setNeededSpend] = useState<MonthlySpendCategory[]>([]);
  const [neededSpendLoading, setNeededSpendLoading] = useState(true);
  const [neededSpendRange, setNeededSpendRange] = useState<{ start: string; end: string } | null>(null);
  const [expandedSpendCategory, setExpandedSpendCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchBudgets();
    fetchYearlyIncome();
    fetchNeededSpend();
    fetchRemainder();
  }, []);

  const fetchRemainder = async () => {
    try {
      setRemainderLoading(true);
      const data = await budgetService.getBudgetRemainder();
      setRemainder(data);
    } catch (error) {
      console.error('Error fetching budget remainder:', error);
      setRemainder(null);
    } finally {
      setRemainderLoading(false);
    }
  };

  // Convert a budget amount to its monthly equivalent based on time_period.
  const monthlyEquivalent = (amount: number, period: 'monthly' | 'yearly'): number => {
    return period === 'yearly' ? amount / 12 : amount;
  };

  // Apply any in-flight drafts to the server's remainder to get the live preview value.
  // Falls back to the original server number when there are no drafts.
  // Accepts a drafts map so callers can choose between the immediate `draftAmounts`
  // (for input echo) and the deferred copy (for the chart).
  const computeLiveRemainder = (drafts: Record<number, number>): number | null => {
    if (!remainder) return null;
    if (Object.keys(drafts).length === 0) return remainder.remainder;
    const adjustedTotal = remainder.breakdown.reduce((sum, b) => {
      const draft = drafts[b.id];
      if (draft != null && isFinite(draft)) {
        return sum + monthlyEquivalent(draft, b.time_period);
      }
      return sum + b.monthly_equivalent;
    }, 0);
    return remainder.monthly_salary - adjustedTotal;
  };

  // useCallback so memoized BudgetCard keeps stable handler refs across
  // unrelated parent re-renders (only the targeted card reconciles).
  const adjustDraft = useCallback((budget: Budget, delta: number) => {
    const original = parseFloat(String(budget.budget_amount));
    setDraftAmounts((prev) => {
      const current = prev[budget.id] ?? original;
      const next = Math.max(0, Math.round((current + delta) * 100) / 100);
      return { ...prev, [budget.id]: next };
    });
  }, []);

  const setDraft = useCallback((budgetId: number, value: number) => {
    setDraftAmounts((prev) => ({ ...prev, [budgetId]: value }));
  }, []);

  const resetDraft = useCallback((budgetId: number) => {
    setDraftAmounts((prev) => {
      const next = { ...prev };
      delete next[budgetId];
      return next;
    });
  }, []);

  const saveDraft = useCallback(async (budget: Budget) => {
    const draft = draftAmountsRef.current[budget.id];
    if (draft == null) return;
    try {
      setSavingBudgetId(budget.id);
      await budgetService.updateBudget(budget.id, { budget_amount: draft });
      showSnackbar(`${budget.category} budget updated`, 'success');
      resetDraft(budget.id);
      await fetchBudgets();
      fetchRemainder();
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to save budget';
      showSnackbar(msg, 'error');
    } finally {
      setSavingBudgetId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetDraft]);

  // Build an N-month cumulative savings projection starting next month.
  // - `savings`: plain cumulative remainder (no interest)
  // - `savingsApr`: cumulative remainder deposited into a savings account at
  //   `apr`% APR, compounded monthly (deposit at start of month, accrue at end)
  // - `preview`: optional second series used when there are unsaved draft edits
  const buildProjectedSavings = (
    monthlyRemainder: number,
    apr: number,
    horizonMonths: number,
    previewMonthly?: number
  ) => {
    const points: { month: string; savings: number; savingsApr: number; preview?: number }[] = [];
    const now = new Date();
    const monthlyRate = (isFinite(apr) ? apr : 0) / 100 / 12;
    let cumulativeSaved = 0;
    let cumulativeApr = 0;
    let cumulativePreview = 0;
    // For long horizons, label by year only after the first 12 months to keep the axis readable.
    const labelByYear = horizonMonths > 24;
    for (let i = 1; i <= horizonMonths; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      cumulativeSaved += monthlyRemainder;
      cumulativeApr = (cumulativeApr + monthlyRemainder) * (1 + monthlyRate);
      const point: { month: string; savings: number; savingsApr: number; preview?: number } = {
        month: labelByYear
          ? d.toLocaleString('en-US', { month: 'short', year: 'numeric' })
          : d.toLocaleString('en-US', { month: 'short', year: '2-digit' }),
        savings: Math.round(cumulativeSaved * 100) / 100,
        savingsApr: Math.round(cumulativeApr * 100) / 100,
      };
      if (previewMonthly != null) {
        cumulativePreview += previewMonthly;
        point.preview = Math.round(cumulativePreview * 100) / 100;
      }
      points.push(point);
    }
    return points;
  };

  const fetchNeededSpend = async () => {
    try {
      setNeededSpendLoading(true);
      const data = await cachedMonthlySpend();
      const allowed = new Set(['mortgage', 'rent', 'utilities', 'groceries', 'grocery']);
      const filtered = (data?.categories || []).filter((c) => allowed.has(String(c.category).toLowerCase()));
      // Order: mortgage, rent, utilities, groceries
      const order = ['mortgage', 'rent', 'utilities', 'groceries', 'grocery'];
      filtered.sort((a, b) => order.indexOf(a.category.toLowerCase()) - order.indexOf(b.category.toLowerCase()));
      setNeededSpend(filtered);
      setNeededSpendRange({ start: data?.start_date || '', end: data?.end_date || '' });
    } catch (error) {
      console.error('Error fetching monthly spend:', error);
      setNeededSpend([]);
    } finally {
      setNeededSpendLoading(false);
    }
  };

  const getNeededCategoryEmoji = (category: string): string => {
    const c = category.toLowerCase();
    if (c === 'mortgage') return '🏠';
    if (c === 'rent') return '🔑';
    if (c === 'utilities') return '💡';
    if (c === 'groceries' || c === 'grocery') return '🛒';
    return '📦';
  };

  const fetchYearlyIncome = async () => {
    try {
      setYearlyIncomeLoading(true);
      const data = await cachedYearlySalary();
      setYearlyIncome(buildMonthlyIncome(data));
    } catch (error) {
      console.error('Error fetching yearly salary:', error);
      setYearlyIncome([]);
    } finally {
      setYearlyIncomeLoading(false);
    }
  };

  // Aggregates per-transaction amounts across all salary streams into one bucket per month.
  // Covers the 12-month window [end_date - 11 months, end_date], filling empty months with 0.
  const buildMonthlyIncome = (data: any): MonthlyIncomePoint[] => {
    if (!data) return [];
    const streams: any[] = Array.isArray(data?.streams) ? data.streams : [];
    const totals = new Map<string, number>();

    for (const stream of streams) {
      const txs: any[] = Array.isArray(stream?.transactions) ? stream.transactions : [];
      for (const tx of txs) {
        const date = String(tx?.date || '');
        const amount = Number(tx?.amount ?? 0);
        if (!date || !isFinite(amount)) continue;
        const key = date.slice(0, 7); // YYYY-MM
        totals.set(key, (totals.get(key) || 0) + amount);
      }
    }

    // Build the 12-month window from the response range (fallback to today)
    const endRef = parseISODate(data?.end_date) || new Date();
    const points: MonthlyIncomePoint[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(endRef.getFullYear(), endRef.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      points.push({
        month: d.toLocaleString('en-US', { month: 'short', year: '2-digit' }),
        income: totals.get(key) || 0,
      });
    }
    return points;
  };

  const parseISODate = (value: any): Date | null => {
    if (!value || typeof value !== 'string') return null;
    const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(value);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3] || '1'));
  };

  // Only fetch AI suggestions for budgets that have crossed the lowest alert threshold —
  // suggestions are only displayed when ≥ threshold, so fetching for healthy budgets is wasted work.
  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const data = await budgetService.getBudgets();
      setBudgets(data);
    } catch (error) {
      showSnackbar('Failed to load budgets', 'error');
      console.error('Error fetching budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (budget?: Budget) => {
    if (budget) {
      setEditingBudget(budget);
      setFormData({
        category: budget.category,
        budget_amount: parseFloat(String(budget.budget_amount)),
        time_period: budget.time_period,
        alert_thresholds: budget.alert_thresholds,
      });
    } else {
      setEditingBudget(null);
      setFormData({
        category: '',
        budget_amount: 0,
        time_period: 'monthly',
        alert_thresholds: [80, 100, 120],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBudget(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.category || formData.budget_amount <= 0) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      let budgetId: number;

      if (editingBudget) {
        const updateData: UpdateBudgetRequest = {
          category: formData.category,
          budget_amount: formData.budget_amount,
          time_period: formData.time_period,
          alert_thresholds: formData.alert_thresholds,
        };
        const updatedBudget = await budgetService.updateBudget(editingBudget.id, updateData);
        budgetId = updatedBudget.id;
        showSnackbar('Budget updated successfully', 'success');
      } else {
        const newBudget = await budgetService.createBudget(formData);
        budgetId = newBudget.id;
        showSnackbar('Budget created successfully', 'success');
      }

      handleCloseDialog();

      // Fetch budgets and refresh the projected savings chart since totals changed.
      await fetchBudgets();
      fetchRemainder();
      void budgetId;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to save budget';
      showSnackbar(errorMessage, 'error');
      console.error('Error saving budget:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this budget?')) {
      try {
        await budgetService.deleteBudget(id);
        showSnackbar('Budget deleted successfully', 'success');

        await fetchBudgets();
        fetchRemainder();
      } catch (error) {
        showSnackbar('Failed to delete budget', 'error');
        console.error('Error deleting budget:', error);
      }
    }
  };

  // Precompute chart inputs so Recharts only re-renders when the deferred draft
  // or server remainder actually changes (not on every keystroke / unrelated state).
  const projectedChart = useMemo(() => {
    if (!remainder) return null;
    const hasDraft = Object.keys(deferredDrafts).length > 0;
    const savedMonthly = remainder.remainder;
    const previewMonthly = computeLiveRemainder(deferredDrafts) ?? savedMonthly;
    const data = buildProjectedSavings(
      savedMonthly,
      deferredApr,
      horizonMonths,
      hasDraft ? previewMonthly : undefined
    );
    const savedColor = savedMonthly >= 0 ? '#0f7a5a' : '#c43e3e';
    return { hasDraft, savedMonthly, previewMonthly, data, savedColor, apr: deferredApr, horizonMonths };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainder, deferredDrafts, deferredApr, horizonMonths]);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Page-level totals
  const totalBudgeted = budgets.reduce((sum, b) => sum + parseFloat(String(b.budget_amount)), 0);
  const totalSpent = budgets.reduce((sum, b) => sum + parseFloat(String(b.current_spending)), 0);
  const totalRemaining = totalBudgeted - totalSpent;
  const overallPct = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;

  // Sort over-budget first, then by % used desc
  const sortedBudgets = [...budgets].sort((a, b) => {
    const aPct = calculatePercentage(a.current_spending, a.budget_amount);
    const bPct = calculatePercentage(b.current_spending, b.budget_amount);
    return bPct - aPct;
  });

  return (
    <Container maxWidth="xl" className="animate-fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontWeight: 600, letterSpacing: '-0.025em', color: '#0a0a0a', mb: 0.5 }}
          >
            Budgets
          </Typography>
          <Typography variant="body2" sx={{ color: '#5c5c5c' }}>
            Track and manage your spending budgets
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleOpenDialog()}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            New Budget
          </Button>
        </Box>
      </Box>

      {/* Page-level summary tiles */}
      {budgets.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, sm: 5 } }}>
              {[
                { label: 'Total budgeted', value: totalBudgeted, color: '#0a0a0a' },
                { label: 'Spent this period', value: totalSpent, color: '#0a0a0a' },
                {
                  label: totalRemaining >= 0 ? 'Remaining' : 'Over budget',
                  value: Math.abs(totalRemaining),
                  color: totalRemaining >= 0 ? '#0f7a5a' : '#c43e3e',
                },
              ].map((tile) => (
                <Box key={tile.label} sx={{ flex: '1 1 160px', minWidth: 140 }}>
                  <Typography variant="body2" sx={{ color: '#8a8a8a', fontWeight: 500, mb: 0.5 }}>
                    {tile.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.02em', color: tile.color, fontVariantNumeric: 'tabular-nums' }}>
                    ${formatCurrency(tile.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500 }}>
                  Overall usage
                </Typography>
                <Typography variant="caption" sx={{ color: '#5c5c5c', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {overallPct.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(overallPct, 100)}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getStatusColor(overallPct),
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Essential spending (mortgage, rent, utilities) */}
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#0a0a0a', letterSpacing: '-0.01em' }}>
                Essential spending
              </Typography>
              <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                Mortgage, rent, utilities and groceries · monthly average
              </Typography>
            </Box>
            {neededSpendRange?.start && neededSpendRange?.end && (
              <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                {neededSpendRange.start} → {neededSpendRange.end}
              </Typography>
            )}
          </Box>

          {neededSpendLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
              <CircularProgress size={22} />
            </Box>
          ) : neededSpend.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
              <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
                No essential spending detected
              </Typography>
            </Box>
          ) : (
            <>
              {/* Summary tiles per category */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                {neededSpend.map((cat) => {
                  const isOpen = expandedSpendCategory === cat.category;
                  return (
                    <Box
                      key={cat.category}
                      onClick={() => setExpandedSpendCategory(isOpen ? null : cat.category)}
                      sx={{
                        flex: '1 1 200px',
                        minWidth: 180,
                        p: 2,
                        border: '1px solid',
                        borderColor: isOpen ? '#0a0a0a' : '#eaeaea',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        bgcolor: isOpen ? '#fafaf6' : '#ffffff',
                        '&:hover': { borderColor: '#1a1a1a' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          {getNeededCategoryEmoji(cat.category)}
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#0a0a0a',
                            textTransform: 'capitalize',
                          }}
                        >
                          {cat.category}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '1.5rem',
                          letterSpacing: '-0.02em',
                          color: '#0a0a0a',
                          fontVariantNumeric: 'tabular-nums',
                          lineHeight: 1.1,
                        }}
                      >
                        ${cat.monthly_average.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block', mt: 0.5 }}>
                        avg / mo · {cat.months_observed} mo observed
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                        ${cat.total_amount.toLocaleString('en-US', { maximumFractionDigits: 2 })} total · {cat.transaction_count} txns
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Transactions detail for expanded category */}
              {expandedSpendCategory && (() => {
                const cat = neededSpend.find((c) => c.category === expandedSpendCategory);
                if (!cat) return null;
                return (
                  <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #f0f0f0' }}>
                    <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                      {cat.category} transactions
                    </Typography>
                    <Box sx={{ mt: 1.25 }}>
                      {cat.transactions
                        .slice()
                        .sort((a, b) => (a.date < b.date ? 1 : -1))
                        .map((tx) => (
                          <Box
                            key={tx.transaction_id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                              py: 1,
                              borderBottom: '1px solid #f5f5f5',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: '0.8125rem', color: '#0a0a0a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {tx.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                                {tx.date} · {tx.detailed.replace(/_/g, ' ').toLowerCase()}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                              ${tx.amount.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Spending by category */}
      <SpendingByCategoryChart />

      {budgets.length === 0 ? (
        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#5c5c5c', fontWeight: 600, mb: 1 }}>
              No budgets yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#8a8a8a', mb: 3 }}>
              Create your first budget to start tracking your spending
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleOpenDialog()}
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
              Create your first budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 0,
            }}
          >
          {sortedBudgets.map((budget, idx) => (
            <BudgetCard
              key={budget.id}
              budget={budget}
              idx={idx}
              draftValue={draftAmounts[budget.id]}
              isSaving={savingBudgetId === budget.id}
              onAdjust={adjustDraft}
              onSetDraft={setDraft}
              onReset={resetDraft}
              onSave={saveDraft}
            />
          ))}
          </Box>
        </Card>
      )}

      {/* Projected savings — next 12 months (cumulative remainder) */}
      <Card sx={{ mt: 3, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#0a0a0a', letterSpacing: '-0.01em' }}>
                Projected savings · next {horizonMonths === 12 ? '12 months' : `${horizonMonths / 12} years`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                Cumulative monthly remainder (salary − budgeted)
              </Typography>
            </Box>
            {/* Horizon toggle + APR control */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  border: '1px solid #eaeaea',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: '#ffffff',
                }}
              >
                {[
                  { label: '1Y', months: 12 },
                  { label: '5Y', months: 60 },
                  { label: '10Y', months: 120 },
                ].map((opt, i) => {
                  const active = horizonMonths === opt.months;
                  return (
                    <Button
                      key={opt.months}
                      onClick={() => setHorizonMonths(opt.months)}
                      disableElevation
                      sx={{
                        minWidth: 0,
                        px: 1.25,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderRadius: 0,
                        borderRight: i < 2 ? '1px solid #eaeaea' : 'none',
                        color: active ? '#0a0a0a' : '#5c5c5c',
                        bgcolor: active ? '#f5f5f5' : 'transparent',
                        '&:hover': { bgcolor: active ? '#f0f0f0' : '#fafafa' },
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#5c5c5c', fontWeight: 500 }}>
                  Savings APR
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={apr}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setApr(isNaN(v) ? 0 : Math.max(0, Math.min(100, v)));
                  }}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ ml: 0 }}>%</InputAdornment>,
                  }}
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.8125rem',
                      fontVariantNumeric: 'tabular-nums',
                      '& input': { py: 0.5, px: 0.75, textAlign: 'right' },
                    },
                  }}
                />
              </Box>
            </Box>
            {remainder && (() => {
              const live = computeLiveRemainder(deferredDrafts) ?? remainder.remainder;
              const hasDraft = Object.keys(deferredDrafts).length > 0;
              const delta = live - remainder.remainder;
              return (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#5c5c5c', fontVariantNumeric: 'tabular-nums' }}>
                    ${live.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} / mo
                  </Typography>
                  {hasDraft && Math.abs(delta) > 0.005 && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: delta >= 0 ? '#0f7a5a' : '#c43e3e',
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {delta >= 0 ? '+' : '−'}${Math.abs(delta).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} preview
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
          {remainderLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !remainder ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
                No projection available
              </Typography>
            </Box>
          ) : (
            projectedChart && (
              <ProjectedSavingsChart
                data={projectedChart.data}
                hasDraft={projectedChart.hasDraft}
                savedColor={projectedChart.savedColor}
                apr={projectedChart.apr}
              />
            )
          )}
        </CardContent>
      </Card>

      {/* Monthly income (past 12 months) — moved below budgets so chart+list pair stays together */}
      <Card sx={{ mt: 3, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="body2" sx={{ color: '#8a8a8a', fontWeight: 500, mb: 0.5 }}>
            Monthly income · past 12 months
          </Typography>
          {yearlyIncomeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
              <CircularProgress size={24} />
            </Box>
          ) : yearlyIncome.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
              <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
                No income data available
              </Typography>
            </Box>
          ) : (
            <MonthlyIncomeChart data={yearlyIncome} />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBudget ? 'Edit Budget' : 'Create New Budget'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <MenuItem value="groceries">🛒 Groceries</MenuItem>
                <MenuItem value="dining">🍽️ Dining</MenuItem>
                <MenuItem value="entertainment">🎬 Entertainment</MenuItem>
                <MenuItem value="transportation">🚗 Transportation</MenuItem>
                <MenuItem value="shopping">🛍️ Shopping</MenuItem>
                <MenuItem value="utilities">💡 Utilities</MenuItem>
                <MenuItem value="healthcare">🏥 Healthcare</MenuItem>
                <MenuItem value="travel">✈️ Travel</MenuItem>
                <MenuItem value="other">📦 Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Budget Amount"
              type="number"
              value={formData.budget_amount}
              onChange={(e) => setFormData({ ...formData, budget_amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <FormControl fullWidth required>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={formData.time_period}
                label="Time Period"
                onChange={(e) => setFormData({ ...formData, time_period: e.target.value as 'monthly' | 'yearly' })}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Alert Thresholds (%)"
              value={formData.alert_thresholds?.join(', ')}
              onChange={(e) => {
                const values = e.target.value.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
                setFormData({ ...formData, alert_thresholds: values });
              }}
              fullWidth
              helperText="Comma-separated percentages (e.g., 80, 100, 120)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingBudget ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Budgets;
